import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import UserTypesEnum from "../../../models/UserTypesEnum";
import ReportTypeEnum from "../../../models/ReportTypeEnum";
import ReportPeriodTypeEnum from "../../../models/ReportPeriodTypeEnum";
import ReportPeriodType from "../../../models/ReportPeriodTypeEnum";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwDateRange from "../../shared/OwDateRange/OwDateRange";
import { Moment } from "moment-timezone";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import ICreateReportDTO from "../../../models/DTO/ICreateReportDTO";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnCreateReportDTO from "../../../models/DTO/IOnCreateReportDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IProcessed_Report from "../../../models/IProcessed_Report";
import Typeahead from "../../shared/Typeahead/Typeahead";
import IOrder_Progress_State from "../../../models/IOrder_Progress_State";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import ICatalog from "../../../models/ICatalog";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IGetItemInOrdersReportDTO from "../../../models/DTO/IGetItemInOrdersReportDTO";
import IOnGetItemInOrdersReportDTO from "../../../models/DTO/IOnGetItemInOrdersReportDTO";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import OrderEntryService from "../../../services/OrderEntryService";
import SupplierService from "../../../services/SupplierService";
import { Socket } from "socket.io-client";
import ICustom_Report from "../../../models/ICustom_Report";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import ICategoriesTypeaheadGetCategoriesDTO from "../../../models/DTO/ICategoriesTypeaheadGetCategoriesDTO";
import ICategory from "../../../models/ICategory";
import ICategoriesOnTypeaheadGetCategoriesDTO from "../../../models/DTO/ICategoriesOnTypeaheadGetCategoriesDTO";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwMultiSelectOption } from "@components/shared/OwMultiSelectOption/OwMultiSelectOption";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import OrdersDetailReportQbo from "./SpecificReportOptions/OrdersDetailReportQbo";
import UIService from "@services/UIService";
import CategoryService from "../../../services/CategoryService";
import FastBoundService from "../../../services/FastBoundService";
import IFastbound_Account from "../../../models/IFastbound_Account";
import { US_STATES } from "../../../services/CountriesConstService";
import ITag from "../../../models/ITag";
import IOnGetTagsDTO from "../../../models/DTO/IOnGetTagsDTO";
import IGetTagsDTO from "../../../models/DTO/IGetTagsDTO";
import IGetVendorsDTO from "@models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "@models/DTO/IOnGetVendorsDTO";
import momentTz from "moment-timezone";
import OwChips from "../../shared/OwChips/OwChips";
import ContextEnum from "../../../models/ContextEnum";
import ScheduledReportsService from "../../../services/ScheduledReportsService";
import IScheduled_Report from "../../../models/IScheduled_Report";
import ICreateScheduledReportDTO from "../../../models/DTO/ICreateScheduledReportDTO";
import IUpdateScheduledReportDTO from "../../../models/DTO/IUpdateScheduledReportDTO";
import ScheduledReportFrequencyEnum from "../../../models/ScheduledReportFrequencyEnum";

type Props = {
    close: (refresh?: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    processedReport?: IProcessed_Report;
    setActivityIndicator: (activityIndicator: boolean) => void;
    customReports: ICustom_Report[];
    /** "instant" (default) emits a one-off report over socket; "scheduled" creates/edits a recurring report via REST. */
    mode?: "instant" | "scheduled";
    /** When provided in scheduled mode, the form edits this existing schedule instead of creating a new one. */
    scheduledReport?: IScheduled_Report;
};

interface IReportOption {
    value: ReportTypeEnum;
    title: string;
    guid?: string;
    uniqueId: string;
}

export const CreateReportSidebar: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const intl = useIntl();

    const renderReportTitle = (option: IReportOption) => {
        // Don't translate custom report titles - they're user-defined names
        if (option.value === ReportTypeEnum.CUSTOM) {
            return option.title;
        }
        // Translate standard report titles
        return intl.formatMessage({ id: option.title, defaultMessage: `_${option.title}_` });
    };

    const getReportOptions = () => {
        const _reportOptions: IReportOption[] = [
            // { value: ReportTypeEnum.SALES_BY_USER, title: "Inventory Report" },
        ];

        let vendorNotesReportTitle = "vendor_notes_review";

        if (supplierModel.vendor_name_override) {
            vendorNotesReportTitle = vendorNotesReportTitle.replace(/vendor/, supplierModel.vendor_name_override);
        }

        switch (userModel.supplierUserToken?.User_Type?.id) {
            case UserTypesEnum.ADMIN:
            case UserTypesEnum.ORDER_MGMT:
            case UserTypesEnum.INVENTORY_MGMT: {
                _reportOptions.push(
                    {
                        value: ReportTypeEnum.ORDERS_DETAIL_REPORT,
                        title: "orders_detail_report",
                        uniqueId: ReportTypeEnum.ORDERS_DETAIL_REPORT,
                    },
                    { value: ReportTypeEnum.SALES_BY_USER, title: "sales_volume_by_user", uniqueId: ReportTypeEnum.SALES_BY_USER },
                    {
                        value: ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL,
                        title: "sales_volume_by_user_product_detail",
                        uniqueId: ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL,
                    },
                    {
                        value: ReportTypeEnum.TOTAL_LOGINS_REPORT,
                        title: "total_logins_report",
                        uniqueId: ReportTypeEnum.TOTAL_LOGINS_REPORT,
                    },
                    { value: ReportTypeEnum.ORDERS_CREATED, title: "orders_created_report", uniqueId: ReportTypeEnum.ORDERS_CREATED },
                    { value: ReportTypeEnum.ORDERS_SUBMITTED, title: "orders_submitted_report", uniqueId: ReportTypeEnum.ORDERS_SUBMITTED },
                    {
                        value: ReportTypeEnum.ITEM_IN_ORDERS_REPORT,
                        title: "item_in_orders_report",
                        uniqueId: ReportTypeEnum.ITEM_IN_ORDERS_REPORT,
                    },
                    { value: ReportTypeEnum.OPEN_ORDER_REPORT, title: "open_orders_report", uniqueId: ReportTypeEnum.OPEN_ORDER_REPORT },
                    {
                        value: ReportTypeEnum.INVENTORY_STOCKOUT_REPORT,
                        title: "inventory_stockout_report",
                        uniqueId: ReportTypeEnum.INVENTORY_STOCKOUT_REPORT,
                    },
                    {
                        value: ReportTypeEnum.INVENTORY_COST_BREAKDOWN_REPORT,
                        title: "inventory_cost_breakdown_report",
                        uniqueId: ReportTypeEnum.INVENTORY_COST_BREAKDOWN_REPORT,
                    },
                    {
                        value: ReportTypeEnum.OPEN_ORDERS_WITH_PARTIAL_SHIPMENTS,
                        title: "open_orders_with_partial_shipments",
                        uniqueId: ReportTypeEnum.OPEN_ORDERS_WITH_PARTIAL_SHIPMENTS,
                    }
                );

                _reportOptions.push({
                    value: ReportTypeEnum.SALES_BY_STATE,
                    title: "sales_by_state",
                    uniqueId: ReportTypeEnum.SALES_BY_STATE,
                });

                _reportOptions.push({
                    value: ReportTypeEnum.SALES_SUMMARY,
                    title: "sales_summary",
                    uniqueId: ReportTypeEnum.SALES_SUMMARY,
                });

                _reportOptions.push({
                    value: ReportTypeEnum.AVG_QTY_BY_ACCOUNT,
                    title: "avg_qty_by_account",
                    uniqueId: ReportTypeEnum.AVG_QTY_BY_ACCOUNT,
                });

                _reportOptions.push({
                    value: ReportTypeEnum.UTM_ATTRIBUTION,
                    title: "utm_attribution",
                    uniqueId: ReportTypeEnum.UTM_ATTRIBUTION,
                });

                if (supplierModel.has_backorders) {
                    _reportOptions.push({
                        value: ReportTypeEnum.BACKORDER_REPORT,
                        title: "backorder_report",
                        uniqueId: ReportTypeEnum.BACKORDER_REPORT,
                    });
                }

                break;
            }
        }

        // Admin only reports.
        switch (userModel.supplierUserToken?.User_Type?.id) {
            case UserTypesEnum.ADMIN:
                _reportOptions.push({
                    value: ReportTypeEnum.ACCOUNT_RESALE_TAX_DOC_REPORT,
                    title: "account_resale_tax_document_report",
                    uniqueId: ReportTypeEnum.ACCOUNT_RESALE_TAX_DOC_REPORT,
                });
                break;
        }

        if (supplierModel) {
            if (supplierModel.has_analytics) {
                switch (userModel.supplierUserToken?.User_Type?.id) {
                    case UserTypesEnum.ADMIN:
                    case UserTypesEnum.ORDER_MGMT:
                    case UserTypesEnum.INVENTORY_MGMT:
                        _reportOptions.push({
                            value: ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER,
                            title: "product_searches_by_customer",
                            uniqueId: ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER,
                        });
                        break;
                }
            }

            if (supplierModel.has_serials) {
                switch (userModel.supplierUserToken?.User_Type?.id) {
                    case UserTypesEnum.ADMIN:
                    case UserTypesEnum.ORDER_MGMT:
                    case UserTypesEnum.INVENTORY_MGMT:
                        _reportOptions.push({
                            value: ReportTypeEnum.SERIALS_IN_ORDERS_REPORT,
                            title: "serials_in_orders_report",
                            uniqueId: ReportTypeEnum.SERIALS_IN_ORDERS_REPORT,
                        });
                        break;
                }
            }

            if (supplierModel.has_commissions) {
                _reportOptions.push({
                    value: ReportTypeEnum.COMMISSION_REPORT,
                    title: "commission_report",
                    uniqueId: ReportTypeEnum.COMMISSION_REPORT,
                });
            }

            if (supplierModel.has_rebates) {
                _reportOptions.push({
                    value: ReportTypeEnum.VOLUME_REBATE_REPORT,
                    title: "volume_rebate_report",
                    uniqueId: ReportTypeEnum.VOLUME_REBATE_REPORT,
                });
            }

            if (supplierModel.has_coop_advertisement) {
                _reportOptions.push({
                    value: ReportTypeEnum.COOP_REPORT,
                    title: "coop_advertisement_report",
                    uniqueId: ReportTypeEnum.COOP_REPORT,
                });
            }

            if (supplierModel.has_spiff) {
                _reportOptions.push({ value: ReportTypeEnum.SPIFF_REPORT, title: "spiff_report", uniqueId: ReportTypeEnum.SPIFF_REPORT });
            }

            if (userModel.userType?.id === UserTypesEnum.ADMIN) {
                _reportOptions.push({
                    value: ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT,
                    title: "inventory_stock_levels_report",
                    uniqueId: ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT,
                });
                _reportOptions.push({
                    value: ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT,
                    title: "inventory_stock_history_transaction_report",
                    uniqueId: ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT,
                });
                _reportOptions.push({
                    value: ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT,
                    title: "accounts_by_date_report",
                    uniqueId: ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT,
                });
                _reportOptions.push({
                    value: ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT,
                    title: "single_category_qty_sales_report",
                    uniqueId: ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT,
                });
            }

            if (supplierModel.Supplier_Integration?.has_fastbound) {
                _reportOptions.push({
                    value: ReportTypeEnum.FASTBOUND_RECONCILIATION,
                    title: "fastbound_reconciliation_report",
                    uniqueId: ReportTypeEnum.FASTBOUND_RECONCILIATION,
                });
            }

            if (supplierModel.Supplier_Integration?.has_unanet) {
                _reportOptions.push({
                    value: ReportTypeEnum.UNANET_FIXED_PRICE_EXPORT,
                    title: "unanet_fixed_price_export_report",
                    uniqueId: ReportTypeEnum.UNANET_FIXED_PRICE_EXPORT,
                });
            }

            if (props.customReports.length) {
                for (const customReport of props.customReports) {
                    _reportOptions.push({
                        value: ReportTypeEnum.CUSTOM,
                        title: customReport.name,
                        guid: customReport.guid,
                        uniqueId: customReport.guid,
                    });
                }
            }
        }

        // Default Reports For All Users
        _reportOptions.push(
            {
                value: ReportTypeEnum.CUSTOMER_NOTES_REVIEW,
                title: vendorNotesReportTitle,
                uniqueId: ReportTypeEnum.CUSTOMER_NOTES_REVIEW,
            },
            {
                value: ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER,
                title: "customer_product_sales",
                uniqueId: ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER,
            },
            {
                value: ReportTypeEnum.PRODUCT_CUSTOMER_SALES,
                title: "product_customer_sales",
                uniqueId: ReportTypeEnum.PRODUCT_CUSTOMER_SALES,
            },
            {
                value: ReportTypeEnum.ACTIVITY_REPORT,
                title: "activity_report",
                uniqueId: ReportTypeEnum.ACTIVITY_REPORT,
            },
            {
                value: ReportTypeEnum.INACTIVE_ACCOUNT_REPORT,
                title: "inactive_account_report",
                uniqueId: ReportTypeEnum.INACTIVE_ACCOUNT_REPORT,
            },
            {
                value: ReportTypeEnum.CATEGORY_QTY_SALES_REPORT,
                title: "all_category_qty_sales_report",
                uniqueId: ReportTypeEnum.CATEGORY_QTY_SALES_REPORT,
            }
        );

        // Scheduled reports currently only support report types whose Excel generation has been ported to the API.
        // Hide everything else until the remaining generators are ported.
        if (props.mode === "scheduled") {
            const scheduledAllowed = new Set<ReportTypeEnum>([
                ReportTypeEnum.PRODUCT_CUSTOMER_SALES,
                ReportTypeEnum.CUSTOMER_NOTES_REVIEW,
                ReportTypeEnum.FASTBOUND_RECONCILIATION,
                ReportTypeEnum.SALES_BY_STATE,
                ReportTypeEnum.AVG_QTY_BY_ACCOUNT,
                ReportTypeEnum.SALES_SUMMARY,
            ]);

            return _reportOptions.filter((option) => scheduledAllowed.has(option.value)).sort((a, b) => (a.title > b.title ? 1 : -1));
        }

        return _reportOptions.sort((a, b) => (a.title > b.title ? 1 : -1));
    };

    const getPeriodTypeOptions = () => {
        const options = [
            { value: ReportPeriodTypeEnum.CURRENT_MONTH, title: "Current Month" },
            { value: ReportPeriodTypeEnum.LAST_MONTH, title: "Last Month" },
            { value: ReportPeriodTypeEnum.CURRENT_QUARTER, title: "Current Quarter" },
            { value: ReportPeriodTypeEnum.LAST_QUARTER, title: "Last Quarter" },
            { value: ReportPeriodTypeEnum.CURRENT_YEAR, title: "Current Year" },
            { value: ReportPeriodTypeEnum.LAST_YEAR, title: "Last Year" },
            { value: ReportPeriodTypeEnum.CUSTOM_RANGE, title: "Custom Range" },
        ];

        // Fiscal presets duplicate the calendar year presets when the fiscal year starts in January.
        if (SupplierService.getFiscalYearStartMonth(supplierModel) !== 1) {
            options.splice(
                6,
                0,
                { value: ReportPeriodTypeEnum.CURRENT_FISCAL_YEAR, title: "Current Fiscal Year" },
                { value: ReportPeriodTypeEnum.LAST_FISCAL_YEAR, title: "Last Fiscal Year" }
            );
        }

        return options;
    };

    const [reportOptions, setReportOptions] = useState<IReportOption[]>(getReportOptions());
    const [periodTypeOptions, setPeriodTypeOptions] = useState<{ value: ReportPeriodTypeEnum; title: string }[]>(getPeriodTypeOptions());
    const [currentReport, setCurrentReport] = useState<ReportTypeEnum>();
    const [showPeriodType, setShowPeriodType] = useState(false);
    const [periodType, setPeriodType] = useState<ReportPeriodType>();
    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();
    const [showSampleSales, setShowSampleSales] = useState(false);
    const [sampleSalesFlag, setSampleSalesFlag] = useState(false);
    const [showCommissionUsersOnly, setShowCommissionUsersOnly] = useState(false);
    const [commissionUsersOnlyFlag, setCommissionUsersOnlyFlag] = useState(false);
    const [showCatalogSelection, setShowCatalogSelection] = useState(false);
    const [showCategorySelection, setShowCategorySelection] = useState(false);
    const [showOrderStatusSelection, setShowOrderStatusSelection] = useState(false);
    const [showIncludeCancelledOrdersOption, setShowIncludeCancelledOrdersOption] = useState(false);
    const [includeCancelledOrders, setIncludeCancelledOrders] = useState(false);
    const [hideMainSearchBtn, setHideMainSearchBtn] = useState(false);
    const [hideCatalogItemSelection, setHideCatalogItemSelection] = useState(false);
    const [showPricingSelection, setShowPricingSelection] = useState(false);
    const [includePricing, setIncludePricing] = useState(false);
    const [separateByCategory, setSeparateByCategory] = useState(false);
    const [reqOptionsSet, setReqOptionsSet] = useState(false);
    const [selectedCustomReport, setSelectedCustomReport] = useState<ICustom_Report | undefined>();

    const [active, setActive] = useState(false);
    const [title, setTitle] = useState("");
    const supplierOrderSteps = OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.PRELIMINARY);

    // Scheduled report mode
    const isScheduled = props.mode === "scheduled";
    const isEditingSchedule = isScheduled && !!props.scheduledReport;
    const timezoneOptions = useMemo(() => momentTz.tz.names().map((zone) => ({ value: zone })), []);
    const [frequency, setFrequency] = useState<ScheduledReportFrequencyEnum>(ScheduledReportFrequencyEnum.DAILY);
    const [dayOfWeek, setDayOfWeek] = useState<number | undefined>(undefined);
    const [dayOfMonth, setDayOfMonth] = useState<number | undefined>(undefined);
    const [timeOfDay, setTimeOfDay] = useState<string>("06:00");
    const [timezone, setTimezone] = useState<string>(supplierModel.timezone || "America/New_York");
    const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
    const [recipientInput, setRecipientInput] = useState<string>("");
    const [scheduleActive, setScheduleActive] = useState<boolean>(true);
    const [savingSchedule, setSavingSchedule] = useState<boolean>(false);

    const dayOfWeekOptions = [
        { value: 0, title: "sunday" },
        { value: 1, title: "monday" },
        { value: 2, title: "tuesday" },
        { value: 3, title: "wednesday" },
        { value: 4, title: "thursday" },
        { value: 5, title: "friday" },
        { value: 6, title: "saturday" },
    ];
    const dayOfMonthOptions = Array.from({ length: 31 }, (_v, i) => ({ value: i + 1, title: String(i + 1) }));
    const frequencyOptions = [
        { value: ScheduledReportFrequencyEnum.DAILY, title: "daily" },
        { value: ScheduledReportFrequencyEnum.WEEKLY, title: "weekly" },
        { value: ScheduledReportFrequencyEnum.MONTHLY, title: "monthly" },
    ];

    const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const [stepSelected, setStepSelected] = useState<{ [key: number]: boolean }>({});
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<string | undefined>(undefined);
    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>();
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ICatalog_Item | undefined>();
    const [reportData, setReportData] = useState<IOnGetItemInOrdersReportDTO>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [clearCategoryTypeahead, setClearCategoryTypeahead] = useState<boolean>(false);
    const [foundCategories, setFoundCategories] = useState<ICategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined>();
    const [includeSubCategories, setIncludeSubCategories] = useState(true);
    const [quotesOnly, setQuotesOnly] = useState(false);
    const [showQboOptions, setShowQboOptions] = useState(false);
    const [quickbooksOnline, setQuickbooksOnline] = useState<{ hasInvoice?: boolean } | undefined>(undefined);

    // Inventory Stock Levels Report filters
    const [activeItemsOnly, setActiveItemsOnly] = useState(true);
    const [stockLevelsCategories, setStockLevelsCategories] = useState<ICategory[]>([]);
    const [selectedStockLevelsCategoryIds, setSelectedStockLevelsCategoryIds] = useState<string[]>([]);

    // Build category options with hierarchy paths for better identification
    const stockLevelsCategoryOptions = useMemo(() => {
        if (stockLevelsCategories.length === 0) return [];

        // Create a map of id -> category for quick lookup
        const categoryMap = new Map<number, ICategory>();
        stockLevelsCategories.forEach((c) => {
            if (c.id) categoryMap.set(c.id, c);
        });

        // Build hierarchy path for each category
        const buildHierarchyPath = (category: ICategory): string => {
            const path: string[] = [];
            let current: ICategory | undefined = category;

            while (current) {
                path.unshift(current.name);
                if (current.parent_category_id) {
                    current = categoryMap.get(current.parent_category_id);
                } else {
                    current = undefined;
                }
            }

            return path.join(" > ");
        };

        return stockLevelsCategories
            .map((c) => ({
                value: String(c.id),
                label: buildHierarchyPath(c),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [stockLevelsCategories]);

    // Order Category Selection for SALES_BY_PRODUCT_BY_CUSTOMER report
    const [showOrderCategorySelection, setShowOrderCategorySelection] = useState(false);
    const [orderCategories, setOrderCategories] = useState<ICategory[]>([]);
    const [selectedOrderCategory, setSelectedOrderCategory] = useState<ICategory | undefined>();

    // Product Customer Sales Report - Multiple product selection
    const [productSelectionMode, setProductSelectionMode] = useState<"products" | "category" | "all-items">("products");
    const [selectedProducts, setSelectedProducts] = useState<ICatalog_Item[]>([]);
    const [selectAllProductsInCategory, setSelectAllProductsInCategory] = useState(false);

    const [showSingleCategoryFilter, setShowSingleCategoryFilter] = useState(false);

    // FastBound Reconciliation Report
    const [fastboundAccounts, setFastboundAccounts] = useState<IFastbound_Account[]>([]);
    const [selectedFastboundAccountId, setSelectedFastboundAccountId] = useState<number | undefined>();
    const [includeDisposed, setIncludeDisposed] = useState(false);
    const [includeDeleted, setIncludeDeleted] = useState(false);

    // Sales by State Report filters
    const [salesByStateSelectedVendor, setSalesByStateSelectedVendor] = useState<any | undefined>();
    const [salesByStateState, setSalesByStateState] = useState<string | undefined>();
    const [salesByStateSelectedTags, setSalesByStateSelectedTags] = useState<ITag[]>([]);
    const [salesByStateFoundTags, setSalesByStateFoundTags] = useState<ITag[]>([]);
    const [foundVendorsForSalesByState, setFoundVendorsForSalesByState] = useState<any[]>([]);

    // Duplicate report state
    const [selectedReportUniqueId, setSelectedReportUniqueId] = useState<string | undefined>();
    const pendingCatalogGuid = useRef<string | undefined>(undefined);
    const pendingCategoryIds = useRef<number[] | undefined>(undefined);
    const pendingOrderCategoryId = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (currentReport === ReportTypeEnum.FASTBOUND_RECONCILIATION) {
            setReqOptionsSet(!!(title.length && selectedFastboundAccountId));
        } else if (currentReport && title.length) {
            setReqOptionsSet(true);
        }
    });

    const onCreateReport = (data: IOnCreateReportDTO) => {
        const notification: INotification = {
            message: "successfully_queued_report",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        props.close(true);
    };

    const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundCatalogItems(data.catalog_items);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    };

    const onItemInOrdersReport = (data: IOnGetItemInOrdersReportDTO) => {
        setReportData(data);
        props.setActivityIndicator(false);
    };

    const onTypeaheadGetCategories = (data: ICategoriesOnTypeaheadGetCategoriesDTO) => {
        setFoundCategories(data.categories);
        props.setActivityIndicator(false);
    };

    const onGetTags = (data: IOnGetTagsDTO) => {
        setSalesByStateFoundTags(data.tags.filter((t) => t.is_order_tag));
    };

    const doTagLookupForSalesByState = (name: string) => {
        const payload: IGetTagsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            name: name || "",
        };
        props.socket.emit(EndpointsEnum.TagsGetTags, payload);
    };

    const selectTagForSalesByState = (tag: ITag) => {
        setSalesByStateSelectedTags([tag]);
    };

    const clearTagForSalesByState = () => {
        setSalesByStateSelectedTags([]);
    };

    const onGetVendorsForSalesByState = (data: IOnGetVendorsDTO) => {
        const vendors = (data.supplierVendorTokens || []).map((token) => token.Vendor);

        setFoundVendorsForSalesByState(vendors);
    };

    const doVendorLookupForSalesByState = (searchValue: string) => {
        if (searchValue.length < 2) return;

        const payload: IGetVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: `name=${searchValue}`,
            limit: 20,
        };

        props.socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    const selectVendorForSalesByState = (vendor: any) => {
        setSalesByStateSelectedVendor(vendor);
    };

    const clearVendorForSalesByState = () => {
        setSalesByStateSelectedVendor(undefined);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ReportOnCreateReport, fn: onCreateReport },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem },
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.ReportOnItemInOrdersReport, fn: onItemInOrdersReport },
        { endpoint: EndpointsEnum.CategoriesOnTypeaheadGetCategories, fn: onTypeaheadGetCategories },
        { endpoint: EndpointsEnum.TagsOnGetTags, fn: onGetTags },
        { endpoint: EndpointsEnum.VendorsOnGetVendors, fn: onGetVendorsForSalesByState },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        const stepSelectedInit: { [key: string]: boolean } = {};

        for (const step of supplierOrderSteps) {
            if (
                step.order_state_id === OrderStatesEnum.PRELIMINARY ||
                step.order_state_id === OrderStatesEnum.SHIPPED ||
                step.order_state_id === OrderStatesEnum.DELIVERED ||
                step.order_state_id === OrderStatesEnum.PICKED_UP
            ) {
                stepSelectedInit[step.order_state_id] = false;
            } else {
                stepSelectedInit[step.order_state_id] = true;
            }
        }

        setStepSelected(stepSelectedInit);

        getCatalogs();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    // Duplicate report / edit schedule: hydrate report-type + options from either source on mount
    useEffect(() => {
        const source = props.processedReport || props.scheduledReport;
        if (!source) return;

        const opts = source.report_options;
        const reportType = source.report_type;

        // Find matching IReportOption and trigger UI visibility flags
        let matchingOption: IReportOption | undefined;
        if (reportType === ReportTypeEnum.CUSTOM && opts.custom_report_guid) {
            matchingOption = reportOptions.find((o) => o.value === ReportTypeEnum.CUSTOM && o.guid === opts.custom_report_guid);
        } else {
            matchingOption = reportOptions.find((o) => o.value === reportType);
        }

        if (matchingOption) {
            setSelectedReportUniqueId(matchingOption.uniqueId);
            handleReportChange(matchingOption);
        }

        // Title
        setTitle(source.title);

        // Period type and dates
        if (opts.periodType) setPeriodType(opts.periodType);
        if (opts.startDate) setStartDate(opts.startDate);
        if (opts.endDate) setEndDate(opts.endDate);

        // Boolean options
        if (opts.includePricing) setIncludePricing(true);
        if (opts.separateByCategory) setSeparateByCategory(true);
        if (opts.includeCancelledOrders) setIncludeCancelledOrders(true);
        if (opts.includeSubCategories !== undefined) setIncludeSubCategories(opts.includeSubCategories);
        if (opts.quotesOnly) setQuotesOnly(true);
        if (opts.activeOnly !== undefined) setActiveItemsOnly(opts.activeOnly);
        if (opts.quickbooksOnline) setQuickbooksOnline(opts.quickbooksOnline);

        // FastBound Reconciliation Report options
        if (reportType === ReportTypeEnum.FASTBOUND_RECONCILIATION) {
            if (opts.fastbound_account_id) setSelectedFastboundAccountId(opts.fastbound_account_id);
            if (opts.include_disposed) setIncludeDisposed(true);
            if (opts.include_deleted) setIncludeDeleted(true);
        }

        // Sales by State Report options
        if (reportType === ReportTypeEnum.SALES_BY_STATE) {
            // Customer hydration for duplicating — we only have the guid, not the full vendor object
            // The customer will need to be re-selected via typeahead on duplicate
            if (opts.state) setSalesByStateState(opts.state);
            // Tag hydration for duplicating — we only have the ID, not the full tag object
            // The tag will need to be re-selected via typeahead on duplicate

            // Hydrate order state toggles from orderStateIds
            if (opts.orderStateIds && Array.isArray(opts.orderStateIds)) {
                const newStepSelected: { [key: number]: boolean } = {};
                for (const step of supplierOrderSteps) {
                    newStepSelected[step.order_state_id] = opts.orderStateIds.includes(step.order_state_id);
                }
                setStepSelected(newStepSelected);
            }
        }

        // Order status toggles
        if (opts.orderStatus && Array.isArray(opts.orderStatus)) {
            const newStepSelected: { [key: number]: boolean } = {};
            for (const step of supplierOrderSteps) {
                newStepSelected[step.order_state_id] = opts.orderStatus.includes(step.order_state_id);
            }
            setStepSelected(newStepSelected);
        }

        // Catalog item placeholder for typeahead display
        if (opts.catalogItemGuid && opts.itemName) {
            setSelectedCatalogItem({
                guid: opts.catalogItemGuid,
                name: opts.itemName,
            } as ICatalog_Item);
        }

        // Store pending async values for resolution when data arrives
        if (opts.catalogGuid) {
            pendingCatalogGuid.current = opts.catalogGuid;
        }

        if (opts.categoryIds && Array.isArray(opts.categoryIds)) {
            pendingCategoryIds.current = opts.categoryIds;
        }

        if (
            opts.categoryId &&
            (reportType === ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER || reportType === ReportTypeEnum.ORDERS_DETAIL_REPORT)
        ) {
            pendingOrderCategoryId.current = opts.categoryId;
        }

        // Product Customer Sales mode
        if (reportType === ReportTypeEnum.PRODUCT_CUSTOMER_SALES) {
            if (opts.productIds && opts.productIds.length > 0) {
                setProductSelectionMode("products");
            } else if (opts.categoryId) {
                setProductSelectionMode("category");
            } else {
                setProductSelectionMode("all-items");
            }
        }
    }, []);

    // Edit schedule: hydrate the scheduling-specific fields from the existing schedule
    useEffect(() => {
        if (!props.scheduledReport) return;

        const s = props.scheduledReport;
        if (s.frequency) setFrequency(s.frequency);
        if (s.day_of_week !== undefined && s.day_of_week !== null) setDayOfWeek(s.day_of_week);
        if (s.day_of_month !== undefined && s.day_of_month !== null) setDayOfMonth(s.day_of_month);
        if (s.time_of_day) setTimeOfDay(s.time_of_day);
        if (s.timezone) setTimezone(s.timezone);
        if (Array.isArray(s.recipient_emails)) setRecipientEmails(s.recipient_emails);
        if (s.active !== undefined && s.active !== null) setScheduleActive(s.active);
    }, []);

    // Duplicate report: set catalog selection once catalogs arrive from socket
    useEffect(() => {
        if (!pendingCatalogGuid.current || catalogs.length === 0) return;

        const catalogGuid = pendingCatalogGuid.current;
        const foundCatalog = catalogs.find((c) => c.guid === catalogGuid);

        if (foundCatalog) {
            setSelectedCatalog(foundCatalog.guid);

            if ((props.processedReport || props.scheduledReport)?.report_type === ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT) {
                loadCategoriesForStockLevelsReport(foundCatalog.guid);
            }
        }

        pendingCatalogGuid.current = undefined;
    }, [catalogs]);

    // Duplicate report: set stock levels category selection once categories load
    useEffect(() => {
        if (!pendingCategoryIds.current || stockLevelsCategories.length === 0) return;

        setSelectedStockLevelsCategoryIds(pendingCategoryIds.current.map((id) => String(id)));
        pendingCategoryIds.current = undefined;
    }, [stockLevelsCategories]);

    // Duplicate report: set order category selection once order categories load
    useEffect(() => {
        if (!pendingOrderCategoryId.current || orderCategories.length === 0) return;

        const category = orderCategories.find((c) => c.id === pendingOrderCategoryId.current);
        if (category) {
            setSelectedOrderCategory(category);
        }
        pendingOrderCategoryId.current = undefined;
    }, [orderCategories]);

    // Builds the report-specific options object. Shared verbatim between one-off reports (socket)
    // and scheduled reports (REST) so the two stay in sync.
    const buildReportOptions = (): any => {
        // tslint:disable-next-line:variable-name
        const report_options: any = {
            startDate,
            endDate,
            periodType,
            reportType: currentReport,
        };

        if (selectedCatalogItem) {
            report_options.itemGuid = selectedCatalogItem.guid;
            report_options.catalogItemGuid = selectedCatalogItem.guid;
            report_options.itemName = selectedCatalogItem.name;
        }

        if (showOrderStatusSelection) {
            const orderStatus: number[] = [];

            for (const key of Object.keys(stepSelected)) {
                if (stepSelected[Number(key)]) {
                    orderStatus.push(Number(key));
                }
            }

            report_options.orderStatus = orderStatus;
        }

        if (selectedCatalog) {
            report_options.catalogGuid = selectedCatalog;
        }

        if (selectedCategory) {
            report_options.categoryGuid = selectedCategory.guid;
        }

        if (includePricing) {
            report_options.includePricing = true;
        }

        // Product Customer Sales Report - Add product IDs or category ID
        if (currentReport === ReportTypeEnum.PRODUCT_CUSTOMER_SALES) {
            if (productSelectionMode === "products" && selectedProducts.length > 0) {
                report_options.productIds = selectedProducts.map((p) => p.id);
            } else if (productSelectionMode === "category" && selectedCategory) {
                report_options.categoryId = selectedCategory.id;
                // If "select all products" is checked, send undefined productIds to get all products in category
                if (selectAllProductsInCategory) {
                    report_options.productIds = undefined;
                }
            }
            report_options.excludeZeroSales = true;
        }

        // Sales By Product By Customer Report - Add category ID and includeCancelledOrders
        if (currentReport === ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER) {
            if (selectedOrderCategory) {
                report_options.categoryId = selectedOrderCategory.id;
            }
            report_options.includeCancelledOrders = includeCancelledOrders;
        }

        // Orders Detail Report - Add category ID
        if (currentReport === ReportTypeEnum.ORDERS_DETAIL_REPORT) {
            if (selectedOrderCategory) {
                report_options.categoryId = selectedOrderCategory.id;
            }
            if (separateByCategory) {
                report_options.separateByCategory = true;
            }
        }

        if (selectedCustomReport) {
            report_options.custom_report_guid = selectedCustomReport.guid;
        }

        if (currentReport === ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT) {
            if (includeSubCategories) {
                report_options.includeSubCategories = includeSubCategories;
            }
        }

        if (currentReport === ReportTypeEnum.ITEM_IN_ORDERS_REPORT) {
            report_options.quotesOnly = quotesOnly;
        }

        // Inventory Stock Levels Report - Add activeOnly and categoryIds
        if (currentReport === ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT) {
            report_options.activeOnly = activeItemsOnly;
            if (selectedStockLevelsCategoryIds.length > 0) {
                report_options.categoryIds = selectedStockLevelsCategoryIds.map((id) => Number(id));
            }
        }

        if (quickbooksOnline) {
            report_options.quickbooksOnline = quickbooksOnline;
        }

        if (currentReport === ReportTypeEnum.SALES_BY_STATE) {
            report_options.orderStateIds = report_options.orderStatus;
            delete report_options.orderStatus;
            if (salesByStateSelectedVendor?.guid) report_options.customerGuid = salesByStateSelectedVendor.guid;
            if (salesByStateState) report_options.state = salesByStateState;
            if (salesByStateSelectedTags.length > 0) report_options.tagId = salesByStateSelectedTags[0].id;
        }

        if (currentReport === ReportTypeEnum.AVG_QTY_BY_ACCOUNT) {
            if (productSelectionMode === "products" && selectedProducts.length > 0) {
                report_options.productIds = selectedProducts.map((p) => p.id);
            } else if (productSelectionMode === "category" && selectedCategory) {
                report_options.categoryId = selectedCategory.id;
            }
        }

        if (currentReport === ReportTypeEnum.FASTBOUND_RECONCILIATION) {
            report_options.fastbound_account_id = selectedFastboundAccountId;
            report_options.include_disposed = includeDisposed;
            report_options.include_deleted = includeDeleted;
        }

        return report_options;
    };

    const createReport = () => {
        if (currentReport) {
            const payload: ICreateReportDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                newReport: {
                    report_type: currentReport,
                    title,
                    report_options: buildReportOptions(),
                },
            };

            props.socket.emit(EndpointsEnum.ReportCreateReport, payload);
        }
    };

    // Validates the scheduling fields. Returns an error message string, or null when valid.
    const getScheduleValidationError = (): string | null => {
        if (!currentReport) return "report_required";
        if (!title.trim()) return "report_title_required";
        if (!TIME_REGEX.test(timeOfDay)) return "invalid_time_of_day";
        if (frequency === ScheduledReportFrequencyEnum.WEEKLY && (dayOfWeek === undefined || dayOfWeek < 0 || dayOfWeek > 6)) {
            return "day_of_week_required";
        }
        if (frequency === ScheduledReportFrequencyEnum.MONTHLY && (dayOfMonth === undefined || dayOfMonth < 1 || dayOfMonth > 31)) {
            return "day_of_month_required";
        }
        if (!timezone) return "timezone_required";
        if (recipientEmails.length === 0) return "recipient_emails_required";
        if (recipientEmails.some((email) => !EMAIL_REGEX.test(email))) return "invalid_recipient_email";
        return null;
    };

    const scheduleValid = isScheduled ? getScheduleValidationError() === null : true;

    const saveScheduledReport = async () => {
        const validationError = getScheduleValidationError();

        if (validationError) {
            props.notifyFn({ eid: "", title: "error", message: validationError });
            return;
        }

        const dto: ICreateScheduledReportDTO = {
            report_type: currentReport as ReportTypeEnum,
            title: title.trim(),
            report_options: buildReportOptions(),
            frequency,
            time_of_day: timeOfDay,
            day_of_week: frequency === ScheduledReportFrequencyEnum.WEEKLY ? dayOfWeek : null,
            day_of_month: frequency === ScheduledReportFrequencyEnum.MONTHLY ? dayOfMonth : null,
            timezone,
            recipient_emails: recipientEmails,
            active: scheduleActive,
        };

        setSavingSchedule(true);
        props.setActivityIndicator(true);

        try {
            const response = isEditingSchedule
                ? await ScheduledReportsService.updateScheduledReport(
                      props.scheduledReport!.guid,
                      dto as IUpdateScheduledReportDTO,
                      userModel,
                      supplierModel
                  )
                : await ScheduledReportsService.createScheduledReport(dto, userModel, supplierModel);

            const nextRun = response.schedule?.next_run_at;
            const nextRunText = nextRun ? momentTz(nextRun).tz(timezone).format("MMM D, YYYY h:mm A z") : "";

            const notification: INotification = {
                message: nextRunText
                    ? `${intl.formatMessage({ id: isEditingSchedule ? "scheduled_report_updated" : "scheduled_report_created" })} ${intl.formatMessage({ id: "next_run" })}: ${nextRunText}`
                    : isEditingSchedule
                      ? "scheduled_report_updated"
                      : "scheduled_report_created",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            props.notifyFn(null, notification, !!nextRunText);
            props.close(true);
        } catch (err: any) {
            props.notifyFn({ eid: "", title: "error", message: err?.message || "error" });
        } finally {
            setSavingSchedule(false);
            props.setActivityIndicator(false);
        }
    };

    const addRecipientEmail = () => {
        const email = recipientInput.trim();
        if (!email) return;
        if (!EMAIL_REGEX.test(email)) {
            props.notifyFn({ eid: "", title: "error", message: "invalid_recipient_email" });
            return;
        }
        if (!recipientEmails.includes(email)) {
            setRecipientEmails([...recipientEmails, email]);
        }
        setRecipientInput("");
    };

    useEffect(() => {
        // console.log("selectedCatalog", selectedCatalog);
    }, [selectedCatalog]);

    const toggleStep = (step: IOrder_Progress_State) => {
        const existingStepSelect = {
            ...stepSelected,
        };

        existingStepSelect[step.order_state_id] = !stepSelected[step.order_state_id];

        setStepSelected(existingStepSelect);
    };

    const doCatalogItemLookup = (name: string) => {
        if (selectedCatalog) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                name,
                catalogGuid: selectedCatalog,
                ignoreProductGroups: true,
                limit: 100,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            props.socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const doCategoryLookup = (name: string) => {
        if (selectedCatalog) {
            const payload: ICategoriesTypeaheadGetCategoriesDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                name,
                catalogGuid: selectedCatalog,
            };

            props.socket.emit(EndpointsEnum.CategoriesTypeaheadGetCategories, payload);

            props.setActivityIndicator(true);
        }
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "CreateReportSidebar",
                fn: "getCatalogs",
            },
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const fetchOrderCategories = useCallback(async () => {
        try {
            const categories = await CategoryService.getCategories(
                { isOrderCategory: true, onlyRootCategories: true },
                userModel,
                supplierModel
            );
            setOrderCategories(categories.filter((c) => !c.hide_category));
        } catch (error) {
            console.error("Error fetching order categories", error);
            setOrderCategories([]);
        }
    }, [userModel, supplierModel]);

    const loadCategoriesForStockLevelsReport = useCallback(
        async (catalogGuid: string) => {
            try {
                const categories = await CategoryService.getCategories(
                    { catalogGuid, onlyRootCategories: false },
                    userModel,
                    supplierModel
                );
                setStockLevelsCategories(categories.filter((c) => !c.hide_category));
            } catch (error) {
                console.error("Error loading categories for stock levels report", error);
                setStockLevelsCategories([]);
            }
        },
        [userModel, supplierModel]
    );

    const handleCatalogChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        let foundCatalog: ICatalog | undefined;

        for (const catalog of catalogs) {
            if (catalog.guid === value) {
                foundCatalog = catalog;
            }
        }

        if (selectedCatalog !== foundCatalog) {
            setSelectedCatalog(undefined);
            setSelectedCatalogItem(undefined);
            setClearTypeahead(true);
            // Reset stock levels categories when catalog changes
            setSelectedStockLevelsCategoryIds([]);
            setStockLevelsCategories([]);
        }

        setSelectedCatalog(foundCatalog ? foundCatalog.guid : undefined);

        // Load categories for stock levels report
        if (foundCatalog && currentReport === ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT) {
            loadCategoriesForStockLevelsReport(foundCatalog.guid);
        }
    };

    const selectCatalogItem = (item: ICatalog_Item) => {
        setSelectedCatalogItem(item);
    };

    const selectCategory = (item: ICategory) => {
        setSelectedCategory(item);
    };

    const clearTypeaheadFn = () => {
        setSelectedCatalogItem(undefined);
        setClearTypeahead(false);
    };

    const clearCategoryTypeaheadFn = () => {
        setSelectedCategory(undefined);
        setClearCategoryTypeahead(false);
    };

    const handleOrderCategoryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const categoryId = Number(e.target.value);
        const category = orderCategories.find((c) => c.id === categoryId);
        setSelectedOrderCategory(category);
    };

    const handleFastboundAccountChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedFastboundAccountId(Number(e.target.value) || undefined);
    };

    // Product Customer Sales Report - Multiple product handlers
    const addProductToSelection = (item: ICatalog_Item) => {
        if (selectedProducts.length < 50 && !selectedProducts.find((p) => p.guid === item.guid)) {
            setSelectedProducts([...selectedProducts, item]);
            setSelectedCatalogItem(undefined);
            setClearTypeahead(true);
        }
    };

    const removeProductFromSelection = (productGuid: string) => {
        setSelectedProducts(selectedProducts.filter((p) => p.guid !== productGuid));
    };

    const handleProductSelectionModeChange = (mode: "products" | "category" | "all-items") => {
        if (mode === "category" && selectedProducts.length > 0) {
            if (window.confirm("Switching to category mode will clear your selected products. Continue?")) {
                setSelectedProducts([]);
                setProductSelectionMode(mode);
                setShowSingleCategoryFilter(true);
            }
        } else if (mode === "products" && selectedCategory) {
            if (window.confirm("Switching to products mode will clear your selected category. Continue?")) {
                setSelectedCategory(undefined);
                setClearCategoryTypeahead(true);
                setProductSelectionMode(mode);
                setShowSingleCategoryFilter(false);
            }
        } else {
            setProductSelectionMode(mode);
        }
    };

    const doSearch = () => {
        if (selectedCatalogItem) {
            const orderStatus: number[] = [];

            for (const key of Object.keys(stepSelected)) {
                if (stepSelected[Number(key)]) {
                    orderStatus.push(Number(key));
                }
            }

            const payload: IGetItemInOrdersReportDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                reportType: ReportTypeEnum.ITEM_IN_ORDERS_REPORT,
                itemGuid: selectedCatalogItem.guid,
                orderStatus,
                periodType: ReportPeriodType.CUSTOM_RANGE,
                quotesOnly,
            };

            props.socket.emit(EndpointsEnum.ReportItemInOrdersReport, payload);

            props.setActivityIndicator(true);
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;

        switch (name) {
            case "sampleSalesFlag":
                setSampleSalesFlag(value);
                break;

            case "commissionUsersOnlyFlag":
                setCommissionUsersOnlyFlag(value);
                break;

            case "title":
                setTitle(value);
                break;

            case "includeCancelledOrdersFlag":
                setIncludeCancelledOrders(value);
                break;

            case "includeSubCategories":
                setIncludeSubCategories(value);
                break;

            case "quotesOnly":
                setQuotesOnly(value);
                break;

            case "includePricing":
                setIncludePricing(value);
                break;

            case "separateByCategory":
                setSeparateByCategory(value);
                break;

            case "includeDisposed":
                setIncludeDisposed(value);
                break;

            case "includeDeleted":
                setIncludeDeleted(value);
                break;
        }
    };

    const handlePeriodTypeChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        setPeriodType(value);
    };

    const setDate = (type: string, date: Moment | null) => {
        const formatStr = "YYYY-MM-DD";

        // if (type === 'endDate') {
        //   formatStr = 'YYYY-MM-DD';
        // }

        if (date) {
            if (type === "startDate") {
                setStartDate(date.format(formatStr));
            } else {
                setEndDate(date.format(formatStr));
            }
        }

        // let state: any = {
        //     [type]: date ? date.format(formatStr) : date,
        // };
        //
        // this.setState(state);
    };

    const handleReportChange = (reportOption: IReportOption) => {
        const name = reportOption.title;
        const value = reportOption.value;
        const guid = reportOption.guid;

        let customReportSelected: ICustom_Report | undefined;

        if (guid) {
            for (const customReport of props.customReports) {
                if (customReport.guid === guid) {
                    customReportSelected = customReport;
                }
            }
        }

        let _showPeriodType = false;
        let _showSampleSales = false;
        let _hideMainSearchBtn = false;
        let _showOrderStatusSelection = false;
        let _showCommissionUsersOnly = false;
        let _showCatalogSelection = false;
        let _showIncludeCancelledOrdersOption = false;
        let _hideCatalogItemSelection = false;
        let _showCategorySelection = false;
        let _showQboOptions = false;
        let _showPricingSelection = false;
        let _showOrderCategorySelection = false;
        let _showSingleCategoryFilter = false;

        switch (value) {
            case ReportTypeEnum.SALES_BY_USER:
            case ReportTypeEnum.SALES_BY_USER_PRODUCT_DETAIL:
                _showCommissionUsersOnly = true;
                _showSampleSales = true;
                _showPeriodType = true;
                break;
            case ReportTypeEnum.SAMPLE_SALES_BY_USER:
            case ReportTypeEnum.ITEM_SALES_REPORT:
                _showSampleSales = true;
                _showPeriodType = true;
                break;
            case ReportTypeEnum.COMMISSION_REPORT:
            case ReportTypeEnum.COMMISSION_TIER_REPORT:
            case ReportTypeEnum.VOLUME_REBATE_REPORT:
            case ReportTypeEnum.COOP_REPORT:
            case ReportTypeEnum.PRODUCT_SEARCHES_BY_CUSTOMER:
            case ReportTypeEnum.ORDERS_CREATED:
            case ReportTypeEnum.ORDERS_SUBMITTED:
            case ReportTypeEnum.ACTIVITY_REPORT:
            case ReportTypeEnum.INACTIVE_ACCOUNT_REPORT:
            case ReportTypeEnum.ACCOUNTS_BY_DATE_REPORT:
            case ReportTypeEnum.UNANET_FIXED_PRICE_EXPORT:
                _showPeriodType = true;
                break;

            case ReportTypeEnum.ORDERS_DETAIL_REPORT:
                _showPeriodType = true;
                _showOrderStatusSelection = true;
                _showQboOptions = true;
                _showPricingSelection = true;
                _showOrderCategorySelection = true;
                break;

            case ReportTypeEnum.TOTAL_LOGINS_REPORT:
                break;

            // Sales Summary requires only a report name — no additional options.
            case ReportTypeEnum.SALES_SUMMARY:
                break;

            case ReportTypeEnum.SALES_BY_PRODUCT_BY_CUSTOMER:
                _showPeriodType = true;
                _showIncludeCancelledOrdersOption = true;
                _showOrderCategorySelection = true;
                break;

            case ReportTypeEnum.PRODUCT_CUSTOMER_SALES:
                _showPeriodType = true;
                _showIncludeCancelledOrdersOption = true;
                _showCatalogSelection = true;
                _showCategorySelection = true;
                break;

            case ReportTypeEnum.BACKORDER_REPORT:
                _showOrderStatusSelection = true;
                _hideMainSearchBtn = false;
                break;

            case ReportTypeEnum.ITEM_IN_ORDERS_REPORT:
                _showPeriodType = true;
                _showOrderStatusSelection = true;
                _hideMainSearchBtn = true;
                _showCatalogSelection = true;
                _showSingleCategoryFilter = false;
                break;

            case ReportTypeEnum.SERIALS_IN_ORDERS_REPORT:
                _showPeriodType = true;
                _showOrderStatusSelection = false;
                _hideMainSearchBtn = true;
                _showCatalogSelection = true;
                _showSingleCategoryFilter = false;
                break;

            case ReportTypeEnum.SPIFF_REPORT:
                _showPeriodType = true;
                _hideMainSearchBtn = true;
                break;

            case ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT:
                _showCatalogSelection = true;
                _showSingleCategoryFilter = false;
                break;

            case ReportTypeEnum.INVENTORY_STOCK_HISTORY_TRANSACTION_REPORT:
                _showCatalogSelection = true;
                _showPeriodType = true;
                break;

            case ReportTypeEnum.CATEGORY_QTY_SALES_REPORT:
                _showPeriodType = true;
                _showCatalogSelection = true;
                _hideCatalogItemSelection = true;
                _showSingleCategoryFilter = true;
                break;

            case ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT:
                _showPeriodType = true;
                _showCatalogSelection = true;
                _hideCatalogItemSelection = true;
                _showCategorySelection = true;
                _showSingleCategoryFilter = true;
                break;

            case ReportTypeEnum.INVENTORY_COST_BREAKDOWN_REPORT:
                _showCatalogSelection = true;
                _hideCatalogItemSelection = true;
                _showSingleCategoryFilter = true;
                break;

            case ReportTypeEnum.SALES_BY_STATE:
                _showPeriodType = true;
                _showOrderStatusSelection = true;
                break;

            case ReportTypeEnum.AVG_QTY_BY_ACCOUNT:
                _showPeriodType = true;
                _showCatalogSelection = true;
                _showCategorySelection = true;
                break;

            // UTM Attribution: period selection only. startDate/endDate/periodType ride in
            // report_options via buildReportOptions; the API generates the Excel and delivers it
            // through the standard processed-report download flow.
            case ReportTypeEnum.UTM_ATTRIBUTION:
                _showPeriodType = true;
                break;

            case ReportTypeEnum.FASTBOUND_RECONCILIATION:
                break;

            case ReportTypeEnum.CUSTOM:
                {
                    if (customReportSelected) {
                        _showPeriodType = customReportSelected.config.create.showPeriodType || true;
                        _showCatalogSelection = customReportSelected.config.create.showCatalogSelection || false;
                        _showOrderStatusSelection = customReportSelected.config.create.showOrderStatusSelection || false;
                        _showSingleCategoryFilter = customReportSelected.config.create.showSingleCategoryFilter || false;
                    }
                }
                break;
        }

        setCurrentReport(value);
        setSelectedReportUniqueId(reportOption.uniqueId);
        setShowPeriodType(_showPeriodType);
        setShowSampleSales(_showSampleSales);
        setHideMainSearchBtn(_hideMainSearchBtn);
        setShowOrderStatusSelection(_showOrderStatusSelection);
        setShowCommissionUsersOnly(_showCommissionUsersOnly);
        setShowCatalogSelection(_showCatalogSelection);
        setShowIncludeCancelledOrdersOption(_showIncludeCancelledOrdersOption);
        setSelectedCustomReport(customReportSelected);
        setHideCatalogItemSelection(_hideCatalogItemSelection);
        setShowCategorySelection(_showCategorySelection);
        setShowQboOptions(_showQboOptions);
        setShowPricingSelection(_showPricingSelection);
        setShowOrderCategorySelection(_showOrderCategorySelection);
        setShowSingleCategoryFilter(_showSingleCategoryFilter);
        setSelectedOrderCategory(undefined);

        if (_showOrderCategorySelection && supplierModel.has_order_categories) {
            fetchOrderCategories();
        }

        // FastBound Reconciliation Report
        if (value === ReportTypeEnum.FASTBOUND_RECONCILIATION) {
            if (!props.processedReport) {
                setTitle("FastBound Reconciliation Report");
            }
            setSelectedFastboundAccountId(undefined);
            setIncludeDisposed(false);
            setIncludeDeleted(false);
            FastBoundService.getAccounts(userModel, supplierModel).then(setFastboundAccounts);
        }

        // Sales by State Report
        if (value === ReportTypeEnum.SALES_BY_STATE) {
            // Default period type to Current Year
            setPeriodType(ReportPeriodTypeEnum.CURRENT_YEAR);

            // Pre-select COMPLETED (order_state_id 9) by default
            const salesByStateStepSelected: { [key: number]: boolean } = {};
            for (const step of supplierOrderSteps) {
                salesByStateStepSelected[step.order_state_id] = step.order_state_id === OrderStatesEnum.COMPLETED;
            }
            setStepSelected(salesByStateStepSelected);

            // Reset Sales by State specific filters
            setSalesByStateSelectedVendor(undefined);
            setSalesByStateState(undefined);
            setSalesByStateSelectedTags([]);
            setSalesByStateFoundTags([]);
            setFoundVendorsForSalesByState([]);
        }

        // UTM Attribution Report: periodType is required by IGetUtmAttributionReportDTO, so seed a
        // sensible default (overridden by the saved value when duplicating an existing report).
        if (value === ReportTypeEnum.UTM_ATTRIBUTION) {
            setPeriodType(ReportPeriodTypeEnum.CURRENT_MONTH);
        }

        // Reset and load categories for stock levels report
        if (value === ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT) {
            setActiveItemsOnly(true);
            setSelectedStockLevelsCategoryIds([]);
            if (selectedCatalog) {
                loadCategoriesForStockLevelsReport(selectedCatalog);
            }
        }
    };

    return (
        <div className={"CreateReportSidebar p-3"}>
            <div className={"d-flex flex-column"}>
                <div className={"ms-auto"}>
                    <OwButton centerIconClass={"fas fa-times"} fn={() => props.close()} className={"btn-danger ms-auto"} />
                </div>

                <OwTitle
                    title={isScheduled ? (isEditingSchedule ? "edit_scheduled_report" : "create_scheduled_report") : "create_report"}
                />
            </div>

            <div>
                <div className={"d-flex row align-items-center mb-3 w-30r"}>
                    <div className={"col-12 mb-3"}>
                        <OwLabel title={"report"} showIsReqAsterik={true} />
                        <OwSelectOption
                            options={reportOptions}
                            valueProp={"uniqueId"}
                            titleProp={"title"}
                            fn={handleReportChange}
                            name={"currentReport"}
                            showBlankFirst={true}
                            translateTitle={false}
                            titleRenderFn={renderReportTitle}
                            emitObject={true}
                            selectedOption={selectedReportUniqueId}
                        />
                    </div>
                    <div className={"col-12 mb-3"}>
                        <OwLabel title={"report_title"} showIsReqAsterik={true} />
                        <OwFnInput fn={handleChange} name={"title"} value={title} isRequired={true} />
                    </div>
                    <div className={"mb-3 col-12 " + (showPeriodType ? "" : "d-none")}>
                        <div className="">
                            <OwLabel title={"range_option"} showIsReqAsterik={true} />
                            <OwSelectOption
                                options={periodTypeOptions}
                                valueProp={"value"}
                                titleProp={"title"}
                                fn={handlePeriodTypeChange}
                                name={"date_range"}
                                showBlankFirst={true}
                                selectedOption={periodType}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                    </div>
                    <div className={"mb-3 col-12 " + (periodType === ReportPeriodType.CUSTOM_RANGE ? "" : "d-none")}>
                        <OwLabel title={"date_range"} />
                        <OwDateRange
                            startDateSelected={startDate ? new Date(startDate + "T00:00:00") : undefined}
                            endDateSelected={endDate ? new Date(endDate + "T00:00:00") : undefined}
                            startFn={(date: Moment | null) => {
                                setDate("startDate", date);
                            }}
                            endFn={(date: Moment | null) => {
                                setDate("endDate", date);
                            }}
                        />
                    </div>
                    <div className={"mb-3 col-12 " + (showSampleSales ? "" : "d-none")}>
                        <div className={"col-12"}>
                            <OwLabel />
                        </div>
                        <div className={"col-12"}>
                            <OwCheckbox
                                fn={handleChange}
                                name={"sampleSalesFlag"}
                                label={"only_sample_sales"}
                                checked={sampleSalesFlag}
                                truthyVal={true}
                                falsyVal={false}
                            />
                        </div>
                    </div>
                    <div className={"mb-3 col-12 " + (showCommissionUsersOnly ? "" : "d-none")}>
                        <div className={"col-12"}>
                            <OwLabel />
                        </div>
                        <div className={"col-12"}>
                            <OwCheckbox
                                fn={handleChange}
                                name={"commissionUsersOnlyFlag"}
                                label={"only_commission_users"}
                                checked={commissionUsersOnlyFlag}
                                truthyVal={true}
                                falsyVal={false}
                            />
                        </div>
                    </div>

                    {showIncludeCancelledOrdersOption ? (
                        <div className={"mb-3 col-12 "}>
                            <div className={"col-12"}>
                                <OwLabel />
                            </div>
                            <div className={"col-12"}>
                                <OwToggle
                                    fn={handleChange}
                                    name={"includeCancelledOrdersFlag"}
                                    label={"include_cancelled_orders"}
                                    active={includeCancelledOrders}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                    ) : null}

                    {showOrderCategorySelection && supplierModel.has_order_categories ? (
                        <div className={"mb-3 col-12"}>
                            <div className={"col-12"}>
                                <OwLabel title={"order_category"} isBold={true} />
                            </div>
                            <div className={"col-12"}>
                                <OwSelectOption
                                    options={orderCategories}
                                    valueProp={"id"}
                                    titleProp={"name"}
                                    fn={handleOrderCategoryChange}
                                    name={"orderCategory"}
                                    showBlankFirst={true}
                                    selectedOption={selectedOrderCategory?.id}
                                    hasSelectedOptionProp={false}
                                />
                            </div>
                        </div>
                    ) : null}

                    {currentReport === ReportTypeEnum.ORDERS_DETAIL_REPORT && (
                        <div className={"mb-3 col-12"}>
                            <div className={"col-12"}>
                                <OwToggle
                                    fn={handleChange}
                                    name={"separateByCategory"}
                                    label={"separate_by_category"}
                                    active={separateByCategory}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                    )}

                    {showCatalogSelection ? (
                        <div className={"mb-3 col-12"}>
                            <div>
                                <div>
                                    <div>
                                        <OwLabel title={"catalog"} isBold={true} isUnderline={true} showIsReqAsterik={true} />
                                    </div>
                                    <OwSelectOption
                                        name={"catalog"}
                                        fn={handleCatalogChange}
                                        options={catalogs}
                                        selectedOption={selectedCatalog}
                                        selectedOptionProp={""}
                                        titleProp={"name"}
                                        valueProp={"guid"}
                                    />
                                </div>

                                {/* Product Customer Sales / Avg Qty By Account Report - Custom UI */}
                                {(currentReport === ReportTypeEnum.PRODUCT_CUSTOMER_SALES ||
                                    currentReport === ReportTypeEnum.AVG_QTY_BY_ACCOUNT) &&
                                selectedCatalog ? (
                                    <div className={"mt-3"}>
                                        <div className={"mb-3"}>
                                            <OwLabel
                                                title={"product_selection_mode"}
                                                isBold={true}
                                                isUnderline={true}
                                                showIsReqAsterik={true}
                                            />
                                            <div className={"d-flex gap-3 mt-2"}>
                                                <div className={"form-check"}>
                                                    <input
                                                        className={"form-check-input"}
                                                        type={"radio"}
                                                        name={"productSelectionMode"}
                                                        id={"mode-all-items"}
                                                        checked={productSelectionMode === "all-items"}
                                                        onChange={() => handleProductSelectionModeChange("all-items")}
                                                    />
                                                    <label className={"form-check-label"} htmlFor={"mode-all-items"}>
                                                        All Items
                                                    </label>
                                                </div>
                                                <div className={"form-check"}>
                                                    <input
                                                        className={"form-check-input"}
                                                        type={"radio"}
                                                        name={"productSelectionMode"}
                                                        id={"mode-products"}
                                                        checked={productSelectionMode === "products"}
                                                        onChange={() => handleProductSelectionModeChange("products")}
                                                    />
                                                    <label className={"form-check-label"} htmlFor={"mode-products"}>
                                                        Specific Products
                                                    </label>
                                                </div>
                                                <div className={"form-check"}>
                                                    <input
                                                        className={"form-check-input"}
                                                        type={"radio"}
                                                        name={"productSelectionMode"}
                                                        id={"mode-category"}
                                                        checked={productSelectionMode === "category"}
                                                        onChange={() => handleProductSelectionModeChange("category")}
                                                    />
                                                    <label className={"form-check-label"} htmlFor={"mode-category"}>
                                                        Category
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {productSelectionMode === "products" && (
                                            <div>
                                                <div className={"mb-2"}>
                                                    <OwLabel
                                                        title={"select_products"}
                                                        isBold={true}
                                                        isUnderline={true}
                                                        showIsReqAsterik={true}
                                                    />
                                                    <div className={"text-muted small"}>{selectedProducts.length} products selected</div>
                                                </div>
                                                <TypeaheadFn
                                                    lookupFn={doCatalogItemLookup}
                                                    availableOptions={foundCatalogItems || []}
                                                    titleProp={"name"}
                                                    selectedOptionFn={addProductToSelection}
                                                    selectedOption={selectedCatalogItem}
                                                    resetTypeahead={clearTypeahead}
                                                    resetTypeaheadCallbackFn={clearTypeaheadFn}
                                                    isScrollable={true}
                                                />
                                                {selectedProducts.length > 0 && (
                                                    <div
                                                        className={"mt-3 p-2 border rounded"}
                                                        style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                        {selectedProducts.map((product) => (
                                                            <div
                                                                key={product.guid}
                                                                className={
                                                                    (UIService.isMainViewLight() ? "bg-light" : "secondary-bg-color") +
                                                                    " d-flex justify-content-between align-items-center mb-2 p-2 rounded"
                                                                }>
                                                                <span>
                                                                    {product.name}
                                                                    <span className={"text-muted ms-2"}>({product.sku})</span>
                                                                </span>
                                                                <button
                                                                    type={"button"}
                                                                    className={"btn btn-sm btn-danger"}
                                                                    onClick={() => removeProductFromSelection(product.guid)}>
                                                                    <i className={"fas fa-times"} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {productSelectionMode === "category" && (
                                            <div>
                                                <div>
                                                    <OwLabel title={"category"} isBold={true} isUnderline={true} showIsReqAsterik={true} />
                                                </div>
                                                <Typeahead
                                                    isSelectableFn={() => true}
                                                    typeaheadApiFn={doCategoryLookup}
                                                    typeaheadItems={foundCategories}
                                                    titleProp={"name"}
                                                    selectedItemTitleProp={"name"}
                                                    selectedItemFn={selectCategory}
                                                    selectedItem={selectedCategory}
                                                    clearTypeahead={clearCategoryTypeahead}
                                                    clearTypeaheadCallback={clearCategoryTypeaheadFn}
                                                    scrollableList={true}
                                                    maxResults={100}
                                                />

                                                {/* Select All Products in Category Option */}
                                                <div className={"mt-3"}>
                                                    <div className={"form-check"}>
                                                        <input
                                                            className={"form-check-input"}
                                                            type={"checkbox"}
                                                            id={"select-all-products"}
                                                            checked={selectAllProductsInCategory}
                                                            onChange={(e) => setSelectAllProductsInCategory(e.target.checked)}
                                                            disabled={!selectedCategory}
                                                        />
                                                        <label className={"form-check-label"} htmlFor={"select-all-products"}>
                                                            Select all products in this category
                                                        </label>
                                                    </div>
                                                    {selectedCategory && selectAllProductsInCategory && (
                                                        <div className={"text-info small mt-1"}>
                                                            All products in &ldquo;{selectedCategory.name}&rdquo; will be included in the
                                                            report
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : null}

                                {/* Other reports - Original UI */}
                                {currentReport !== ReportTypeEnum.PRODUCT_CUSTOMER_SALES &&
                                currentReport !== ReportTypeEnum.AVG_QTY_BY_ACCOUNT &&
                                selectedCatalog &&
                                !hideCatalogItemSelection ? (
                                    <div className={"mt-3"}>
                                        <div>
                                            <OwLabel title={"catalog_item"} isBold={true} isUnderline={true} showIsReqAsterik={true} />
                                        </div>
                                        <TypeaheadFn
                                            lookupFn={doCatalogItemLookup}
                                            availableOptions={foundCatalogItems || []}
                                            titleProp={"name"}
                                            selectedOptionFn={selectCatalogItem}
                                            selectedOption={selectedCatalogItem}
                                            resetTypeahead={clearTypeahead}
                                            resetTypeaheadCallbackFn={clearTypeaheadFn}
                                            isScrollable={true}
                                        />
                                    </div>
                                ) : null}
                                {currentReport !== ReportTypeEnum.PRODUCT_CUSTOMER_SALES &&
                                currentReport !== ReportTypeEnum.AVG_QTY_BY_ACCOUNT &&
                                selectedCatalog &&
                                showCatalogSelection &&
                                showSingleCategoryFilter ? (
                                    <div className={"mt-3"}>
                                        <div>
                                            <OwLabel title={"category"} isBold={true} isUnderline={true} showIsReqAsterik={false} />
                                        </div>
                                        <Typeahead
                                            isSelectableFn={() => true}
                                            typeaheadApiFn={doCategoryLookup}
                                            typeaheadItems={foundCategories}
                                            titleProp={"name"}
                                            selectedItemTitleProp={"name"}
                                            selectedItemFn={selectCategory}
                                            selectedItem={selectedCategory}
                                            clearTypeahead={clearCategoryTypeahead}
                                            clearTypeaheadCallback={clearCategoryTypeaheadFn}
                                            scrollableList={true}
                                            maxResults={100}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            {currentReport === ReportTypeEnum.SINGLE_CATEGORY_QTY_SALES_REPORT ? (
                                <div>
                                    <OwCheckbox
                                        name={"includeSubCategories"}
                                        label={"include_subcategories"}
                                        fn={handleChange}
                                        truthyVal={true}
                                        falsyVal={false}
                                        checked={includeSubCategories}
                                    />
                                </div>
                            ) : null}

                            {currentReport === ReportTypeEnum.INVENTORY_STOCK_LEVELS_REPORT && selectedCatalog ? (
                                <div className={"mt-3"}>
                                    <div className={"mb-3"}>
                                        <OwToggle
                                            fn={() => setActiveItemsOnly(!activeItemsOnly)}
                                            name={"activeItemsOnly"}
                                            label={"active_items_only"}
                                            active={activeItemsOnly}
                                            labelOnRight={true}
                                        />
                                    </div>
                                    {stockLevelsCategoryOptions.length > 0 ? (
                                        <div>
                                            <OwLabel title={"categories"} isBold={true} />
                                            <OwMultiSelectOption
                                                options={stockLevelsCategoryOptions}
                                                selectedOptions={selectedStockLevelsCategoryIds}
                                                onChange={setSelectedStockLevelsCategoryIds}
                                                placeholder={"All Categories"}
                                                maxDisplay={3}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {supplierModel.has_quotes && currentReport === ReportTypeEnum.ITEM_IN_ORDERS_REPORT ? (
                        <div className={"mb-3 col-12"}>
                            <div className={"col-12"}>
                                <OwLabel />
                            </div>
                            <div className={"col-12"}>
                                <OwToggle
                                    fn={handleChange}
                                    name={"quotesOnly"}
                                    label={"quotes_only"}
                                    active={quotesOnly}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                    ) : null}

                    {showOrderStatusSelection && !quotesOnly ? (
                        <div className={"d-flex flex-column mt-3"}>
                            {supplierOrderSteps
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((step) => {
                                    return (
                                        <div key={step.order_state_id} className={"col-auto"}>
                                            <OwToggle
                                                name={step.name}
                                                label={step.name}
                                                fn={() => {
                                                    toggleStep(step);
                                                }}
                                                active={stepSelected[Number(step.order_state_id)]}
                                                labelOnRight={true}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    ) : null}

                    {currentReport === ReportTypeEnum.SALES_BY_STATE && (
                        <div className={"mt-3"}>
                            {/* Customer Typeahead */}
                            <div className={"mb-3"}>
                                <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} />
                                <TypeaheadFn
                                    lookupFn={doVendorLookupForSalesByState}
                                    availableOptions={foundVendorsForSalesByState}
                                    titleProp={"name"}
                                    selectedOptionFn={selectVendorForSalesByState}
                                    selectedOption={salesByStateSelectedVendor}
                                    clearSelectionFn={clearVendorForSalesByState}
                                    isScrollable={true}
                                    placeholder={"search"}
                                />
                            </div>

                            {/* State Dropdown */}
                            <div className={"mb-3"}>
                                <OwLabel title={"state"} />
                                <OwSelectOption
                                    options={US_STATES}
                                    valueProp={"code"}
                                    titleProp={"name"}
                                    fn={(e: ChangeEvent<HTMLSelectElement>) => {
                                        setSalesByStateState(e.target.value || undefined);
                                    }}
                                    name={"salesByStateState"}
                                    showBlankFirst={true}
                                    selectedOption={salesByStateState}
                                    hasSelectedOptionProp={false}
                                />
                            </div>

                            {/* Tag Lookup */}
                            <div className={"mb-3"}>
                                <OwLabel title={"tag"} />
                                <TypeaheadFn
                                    lookupFn={doTagLookupForSalesByState}
                                    availableOptions={salesByStateFoundTags}
                                    titleProp={"name"}
                                    selectedOptionFn={selectTagForSalesByState}
                                    selectedOption={salesByStateSelectedTags[0]}
                                    clearSelectionFn={clearTagForSalesByState}
                                    isScrollable={true}
                                    placeholder={"search"}
                                />
                            </div>
                        </div>
                    )}

                    {showPricingSelection && (
                        <div className={"mt-5 mb-2"}>
                            <div className={"col-12"}>
                                <OwToggle
                                    fn={handleChange}
                                    name={"includePricing"}
                                    label={"include_pricing"}
                                    active={includePricing}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                    )}

                    {currentReport === ReportTypeEnum.FASTBOUND_RECONCILIATION && (
                        <div className={"mb-3 col-12"}>
                            <div className={"mb-2"}>
                                <OwLabel title={"fastbound_account"} isBold={true} showIsReqAsterik={true} />
                                <OwSelectOption
                                    options={fastboundAccounts}
                                    valueProp={"id"}
                                    titleProp={"name"}
                                    fn={handleFastboundAccountChange}
                                    name={"fastboundAccount"}
                                    showBlankFirst={true}
                                    selectedOption={selectedFastboundAccountId}
                                    hasSelectedOptionProp={false}
                                />
                            </div>
                            <div className={"mb-1"}>
                                <OwToggle
                                    fn={handleChange}
                                    name={"includeDisposed"}
                                    label={"include_disposed_items"}
                                    active={includeDisposed}
                                    labelOnRight={true}
                                />
                            </div>
                            <div>
                                <OwToggle
                                    fn={handleChange}
                                    name={"includeDeleted"}
                                    label={"include_deleted_items"}
                                    active={includeDeleted}
                                    labelOnRight={true}
                                />
                            </div>
                        </div>
                    )}

                    {showQboOptions && supplierModel.Supplier_Integration?.has_qbo ? (
                        <OrdersDetailReportQbo onChange={(quickbooksOnline) => setQuickbooksOnline(quickbooksOnline)} />
                    ) : null}

                    {isScheduled ? (
                        <div className={"ScheduleOptions border-top pt-3 mt-2"}>
                            <div className={"col-12 mb-3"}>
                                <OwLabel title={"frequency"} showIsReqAsterik={true} />
                                <OwSelectOption
                                    options={frequencyOptions}
                                    valueProp={"value"}
                                    titleProp={"title"}
                                    fn={(e: any) => setFrequency(e.target.value as ScheduledReportFrequencyEnum)}
                                    name={"frequency"}
                                    selectedOption={frequency}
                                    hasSelectedOptionProp={false}
                                    translateTitle={true}
                                />
                            </div>

                            {frequency === ScheduledReportFrequencyEnum.WEEKLY ? (
                                <div className={"col-12 mb-3"}>
                                    <OwLabel title={"day_of_week"} showIsReqAsterik={true} />
                                    <OwSelectOption
                                        options={dayOfWeekOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        fn={(e: any) => setDayOfWeek(Number(e.target.value))}
                                        name={"day_of_week"}
                                        showBlankFirst={true}
                                        selectedOption={dayOfWeek}
                                        hasSelectedOptionProp={false}
                                        translateTitle={true}
                                    />
                                </div>
                            ) : null}

                            {frequency === ScheduledReportFrequencyEnum.MONTHLY ? (
                                <div className={"col-12 mb-3"}>
                                    <OwLabel title={"day_of_month"} showIsReqAsterik={true} />
                                    <OwSelectOption
                                        options={dayOfMonthOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        fn={(e: any) => setDayOfMonth(Number(e.target.value))}
                                        name={"day_of_month"}
                                        showBlankFirst={true}
                                        selectedOption={dayOfMonth}
                                        hasSelectedOptionProp={false}
                                        translateTitle={false}
                                    />
                                </div>
                            ) : null}

                            <div className={"col-12 mb-3"}>
                                <OwLabel title={"time_of_day"} showIsReqAsterik={true} />
                                <OwFnInput
                                    type={"time"}
                                    name={"time_of_day"}
                                    value={timeOfDay}
                                    fn={(e: any) => setTimeOfDay(e.target.value)}
                                />
                            </div>

                            <div className={"col-12 mb-3"}>
                                <OwLabel title={"timezone"} showIsReqAsterik={true} />
                                <OwSelectOption
                                    options={timezoneOptions}
                                    valueProp={"value"}
                                    titleProp={"value"}
                                    fn={(e: any) => setTimezone(e.target.value)}
                                    name={"timezone"}
                                    selectedOption={timezone}
                                    hasSelectedOptionProp={false}
                                    translateTitle={false}
                                />
                            </div>

                            <div className={"col-12 mb-3"}>
                                <OwLabel title={"recipient_emails"} showIsReqAsterik={true} />
                                <div className={"d-flex"}>
                                    <OwFnInput
                                        type={"email"}
                                        name={"recipient_email"}
                                        value={recipientInput}
                                        placeholder={"add_recipient_email"}
                                        fn={(e: any) => setRecipientInput(e.target.value)}
                                        onKeyDownFn={(e: any) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addRecipientEmail();
                                            }
                                        }}
                                        noOuterDiv={true}
                                    />
                                    <OwButton title={"add"} fn={addRecipientEmail} className={"btn-primary ms-2"} />
                                </div>
                                <div className={"mt-2"}>
                                    <OwChips
                                        updatedFn={(chips) => setRecipientEmails(typeof chips === "string" ? [chips] : chips)}
                                        context={ContextEnum.PRIMARY}
                                        chips={recipientEmails}
                                    />
                                </div>
                            </div>

                            <div className={"col-12 mb-3"}>
                                <OwToggle
                                    name={"active"}
                                    label={"active"}
                                    fn={(e: any) => setScheduleActive(e.target.value)}
                                    active={scheduleActive}
                                />
                            </div>

                            <div className={"col-12 mb-2"}>
                                <OwLabel title={"delivered_as_excel"} />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={""}>
                <div className={"col-12 mb-1"}>
                    <OwLabel />
                </div>
                <div>
                    {isScheduled ? (
                        <OwButton
                            title={isEditingSchedule ? "save" : "create"}
                            fn={saveScheduledReport}
                            active={savingSchedule}
                            disableOnActive={true}
                            disabled={!reqOptionsSet || !scheduleValid}
                        />
                    ) : (
                        <OwButton title={"create"} fn={createReport} active={active} disableOnActive={true} disabled={!reqOptionsSet} />
                    )}
                </div>
            </div>
        </div>
    );
};
